import {
  Box,
  Paper,
  Button,
  useTheme,
  TextField,
  FormLabel,
  Typography,
  DialogTitle,
  Autocomplete,
  Grid2 as Grid,
  svgIconClasses,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import DeleteIcon from '@mui/icons-material/Delete';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { gray } from 'providers/theme/themePrimitives';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DocumentPreviewDialog from '../document-preview-dialog';
import { renderAutoCompleteLocaleText, renderDatePickerLocaleText } from 'utils';
import { LoadingBackdrop, ResponsiveDialog, ResponsiveDialogActions, ResponsiveDialogContent } from 'components';

export type CreateHolidayFormParams = {
  holidayType: string;
  startTime: Dayjs | null;
  endTime: Dayjs | null;
  document: string; // Base64 string
  documentName: string;
};

const defaultValues: CreateHolidayFormParams = {
  holidayType: '',
  startTime: null,
  endTime: null,
  document: '',
  documentName: '',
};

type CreateHolidayDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
};

export const HOLIDAY_TYPES = [
  { Id: 'Y', Text: 'Yıllık izin' },
  { Id: 'D', Text: 'Doğum izni' },
  { Id: 'O', Text: 'Ölüm izni' },
  { Id: 'M', Text: 'Mazeret izni' },
  { Id: 'E', Text: 'Evlilik izni' },
  { Id: 'H', Text: 'Hastalık izni' },
];

const CreateHolidayDialog = ({ open, handleClose: closeDialog, refresh }: CreateHolidayDialogProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form validations & form state
  const schema = yup.object().shape({
    holidayType: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    startTime: yup
      .mixed<Dayjs>()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    endTime: yup
      .mixed<Dayjs>()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .test(
        'is-after-start',
        intl.formatMessage({
          id: 'validations.endDateMustBeAfterStartDate',
          defaultMessage: 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır',
        }),
        function (value) {
          const { startTime } = this.parent;
          if (!startTime || !value) return true;
          return dayjs(value).isAfter(dayjs(startTime));
        }
      ),
    document: yup.string().required(
      intl.formatMessage({
        id: 'personnelHoliday.documentRequired',
        defaultMessage: 'Lütfen izin belgesini yükleyiniz',
      })
    ),
    documentName: yup.string().required(),
  });

  const {
    reset,
    control,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateHolidayFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  const watchDocumentName = watch('documentName');
  const watchDocument = watch('document');
  const watchHolidayType = watch('holidayType');

  // Validate and read file as base64
  const processFile = (file: File) => {
    if (file.type !== 'application/pdf') {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.onlyPdfAllowed',
          defaultMessage: 'Yalnızca PDF formatında dosya yükleyebilirsiniz.',
        }),
        { variant: 'warning' }
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.fileSizeExceeded',
          defaultMessage: "Dosya boyutu 10MB'ı aşamaz.",
        }),
        { variant: 'warning' }
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64Clean = event.target?.result as string;
      setValue('document', base64Clean, { shouldValidate: true });
      setValue('documentName', file.name, { shouldValidate: true });
    };
    reader.onerror = () => {
      enqueueSnackbar(
        intl.formatMessage({
          id: 'personnelHoliday.fileReadError',
          defaultMessage: 'Dosya okunurken bir hata oluştu.',
        }),
        { variant: 'error' }
      );
    };
    reader.readAsDataURL(file);
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const clearUploadedFile = () => {
    setValue('document', '', { shouldValidate: true });
    setValue('documentName', '', { shouldValidate: true });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Submit form
  const onSubmit = async (data: CreateHolidayFormParams) => {
    try {
      setLoading(true);

      const payload = {
        holidayType: data.holidayType,
        startTime: data.startTime ? data.startTime.toISOString() : '',
        endTime: data.endTime ? data.endTime.toISOString() : '',
        document: data.document.includes(',') ? data.document.split(',')[1] : data.document,
      };

      const response = await apiService.createPersonnelHoliday(payload);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage id="personnelHoliday.createSuccess" defaultMessage="İzin talebi başarıyla oluşturuldu." />,
          { variant: 'success' }
        );
        handleClose();
        await refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    closeDialog();
    reset();
  };

  const selectedHolidayTypeObj = HOLIDAY_TYPES.find((x) => x.Id === watchHolidayType) || null;

  return (
    <>
      <ResponsiveDialog maxWidth="md" open={open} onClose={handleClose}>
        <DialogTitle>
          <FormattedMessage id="personnelHoliday.createTitle" defaultMessage="İzin Talebi Oluştur" />
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <ResponsiveDialogContent sx={{ gap: 2.5 }}>
            <DialogContentText>
              <FormattedMessage
                id="personnelHoliday.createSubtext"
                defaultMessage="İzin detaylarını girerek yeni bir izin talebinde bulunabilirsiniz. Lütfen onaylı izin belgesini de ekleyiniz."
              />
            </DialogContentText>
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12 }}>
                <FormLabel htmlFor="holidayType" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  <FormattedMessage id="personnelHoliday.holidayType" defaultMessage="İzin Türü" />
                </FormLabel>
                <Autocomplete
                  options={HOLIDAY_TYPES}
                  value={selectedHolidayTypeObj}
                  onChange={(_, newValue) => {
                    setValue('holidayType', newValue ? newValue.Id : '', { shouldValidate: true });
                  }}
                  {...renderAutoCompleteLocaleText(intl)}
                  getOptionLabel={(option) => option.Text}
                  slotProps={{
                    paper: { sx: { backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[900] } },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        '& .MuiButtonBase-root': {
                          border: 'none',
                          backgroundColor: 'inherit',
                          width: '2.25rem',
                          height: '2.25rem',
                          padding: '0.25rem',
                          [`& .${svgIconClasses.root}`]: { fontSize: '1rem' },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' ? gray[100] : gray[800],
                          },
                        },
                      }}
                      error={!!errors.holidayType}
                      helperText={errors.holidayType?.message}
                      placeholder={intl.formatMessage({
                        id: 'personnelHoliday.selectType',
                        defaultMessage: 'İzin Türü Seçiniz',
                      })}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel htmlFor="startTime" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  <FormattedMessage id="activityManaging.startDate" defaultMessage="Başlangıç Tarihi" />
                </FormLabel>
                <Controller
                  name="startTime"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateTimePicker
                      {...field}
                      ampm={false}
                      localeText={renderDatePickerLocaleText(intl)}
                      slotProps={{
                        textField: {
                          placeholder: intl.locale === 'tr' ? 'GG.AA.YYYY ss:dd' : 'MM/DD/YYYY hh:mm',
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel htmlFor="endTime" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  <FormattedMessage id="activityManaging.endDate" defaultMessage="Bitiş Tarihi" />
                </FormLabel>
                <Controller
                  name="endTime"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <DateTimePicker
                      {...field}
                      ampm={false}
                      localeText={renderDatePickerLocaleText(intl)}
                      slotProps={{
                        textField: {
                          placeholder: intl.locale === 'tr' ? 'GG.AA.YYYY ss:dd' : 'MM/DD/YYYY hh:mm',
                          fullWidth: true,
                          error: !!error,
                          helperText: error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormLabel sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                  <FormattedMessage id="personnelHoliday.document" defaultMessage="İzin Belgesi" />
                </FormLabel>

                {watchDocument ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderColor: theme.palette.divider,
                      borderRadius: 1,
                      backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                      <CloudUploadIcon color="primary" />
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {watchDocumentName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => setPreviewOpen(true)}>
                        <FormattedMessage id="personnelHoliday.preview" defaultMessage="Önizle" />
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteIcon />}
                        onClick={clearUploadedFile}>
                        <FormattedMessage id="dashboard.delete" defaultMessage="Sil" />
                      </Button>
                    </Box>
                  </Paper>
                ) : (
                  <Box
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: `2px dashed ${errors.document ? theme.palette.error.main : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s, background-color 0.2s',
                      backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'rgba(255,255,255,0.02)',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)',
                      },
                    }}>
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      accept=".pdf"
                      onChange={handleFileChange}
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      <FormattedMessage
                        id="personnelHoliday.uploadClick"
                        defaultMessage="Dosya yüklemek için tıklayın veya sürükleyip bırakın"
                      />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      PDF (Maks. 10MB)
                    </Typography>
                    {errors.document && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        {errors.document.message}
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </ResponsiveDialogContent>
          <ResponsiveDialogActions>
            <Button onClick={handleClose} disabled={loading}>
              <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
            </Button>
            <Button variant="contained" type="submit" disabled={loading}>
              <FormattedMessage id="forgotPassword.continue" defaultMessage="Gönder" />
            </Button>
          </ResponsiveDialogActions>
        </Box>
        <LoadingBackdrop open={loading} />
      </ResponsiveDialog>

      {watchDocument && (
        <DocumentPreviewDialog
          open={previewOpen}
          handleClose={() => setPreviewOpen(false)}
          documentData={watchDocument}
          documentName={watchDocumentName}
        />
      )}
    </>
  );
};

export default CreateHolidayDialog;
